import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import {
  $createParagraphNode,
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  type EditorState,
  KEY_ENTER_COMMAND,
  type LexicalEditor,
  PASTE_COMMAND,
} from 'lexical';

import classNames from '@/utils/classnames';

import { $isFileMentionNode, FileMentionNode } from './FileMentionNode';
import { FileMentionPlugin } from './FileMentionPlugin';

export interface MentionEditorHandle {
  /** The composer's plain-text message, with each mention serialized as its literal `@path`
   * inline where it was typed. */
  getText(): string;
  clear(): void;
}

interface MentionEditorProps {
  /** Sandbox resource prefix (`/api/v1/agent-sessions/{sessionId}`) — where the mention dropdown
   * fetches candidate files from. */
  base: string;
  placeholder: string;
  disabled?: boolean;
  className?: string;
  /** Plain Enter submits; Shift+Enter still inserts a line break (handled by the editor itself). */
  onSubmit: () => void;
  /** Hands an image-carrying paste off to the composer's own attachment handling instead of
   * letting Lexical insert it as text. */
  onPasteFiles: (event: ClipboardEvent) => void;
  onEmptyChange: (empty: boolean) => void;
}

function serialize(editor: LexicalEditor): string {
  return editor.getEditorState().read(() => {
    const parts: string[] = [];
    for (const topLevel of $getRoot().getChildren()) {
      if (!$isElementNode(topLevel)) continue;
      for (const child of topLevel.getChildren()) {
        if ($isFileMentionNode(child)) parts.push(`@${child.getPath()}`);
        else if ($isLineBreakNode(child)) parts.push('\n');
        else if ($isTextNode(child)) parts.push(child.getTextContent());
      }
    }
    return parts.join('');
  });
}

function isEmptyState(editorState: EditorState): boolean {
  return editorState.read(() => $getRoot().getTextContent().trim().length === 0);
}

/** Wires the imperative handle, submit-on-Enter, paste handoff and editable/disabled sync — split
 * out from `MentionEditor` because all of it needs `useLexicalComposerContext`, which only works
 * inside the `LexicalComposer` tree the outer component renders. */
const EditorBridge = forwardRef<
  MentionEditorHandle,
  Pick<MentionEditorProps, 'disabled' | 'onSubmit' | 'onPasteFiles' | 'onEmptyChange'>
>(({ disabled, onSubmit, onPasteFiles, onEmptyChange }, ref) => {
  const [editor] = useLexicalComposerContext();

  useImperativeHandle(
    ref,
    () => ({
      getText: () => serialize(editor),
      clear: () =>
        editor.update(() => {
          $getRoot().clear().append($createParagraphNode());
        }),
    }),
    [editor],
  );

  useEffect(() => editor.setEditable(!disabled), [editor, disabled]);

  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (event?.shiftKey) return false;
          event?.preventDefault();
          onSubmit();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, onSubmit],
  );

  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          if (!(event instanceof ClipboardEvent) || !event.clipboardData?.files.length) return false;
          onPasteFiles(event);
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor, onPasteFiles],
  );

  useEffect(
    () => editor.registerUpdateListener(({ editorState }) => onEmptyChange(isEmptyState(editorState))),
    [editor, onEmptyChange],
  );

  return null;
});
EditorBridge.displayName = 'EditorBridge';

export const MentionEditor = forwardRef<MentionEditorHandle, MentionEditorProps>(
  ({ base, placeholder, disabled, className, onSubmit, onPasteFiles, onEmptyChange }, ref) => {
    const initialConfig = useMemo(
      () => ({
        namespace: 'agent-session-composer',
        nodes: [FileMentionNode],
        onError: (error: Error) => {
          throw error;
        },
      }),
      [],
    );

    return (
      <LexicalComposer initialConfig={initialConfig}>
        <div className="relative w-full">
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                // Focus target for anything outside the composer that wants to hand the user
                // straight to it — the plan pane's "N passages staged" strip does.
                data-composer-input
                aria-placeholder={placeholder}
                placeholder={
                  <div
                    className={classNames(
                      'pointer-events-none absolute top-0 left-0 w-full select-none text-sm text-fg-disabled',
                      className,
                    )}
                  >
                    {placeholder}
                  </div>
                }
                className={classNames(
                  'min-h-11 w-full bg-transparent text-sm text-fg outline-none',
                  disabled && 'cursor-not-allowed opacity-50',
                  className,
                )}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <FileMentionPlugin base={base} />
        <EditorBridge
          ref={ref}
          disabled={disabled}
          onSubmit={onSubmit}
          onPasteFiles={onPasteFiles}
          onEmptyChange={onEmptyChange}
        />
      </LexicalComposer>
    );
  },
);
MentionEditor.displayName = 'MentionEditor';
